/**
 * The simple workspace: one page, three columns.
 *
 *   ┌ header ─────────────────────────────────────────────┐
 *   │ pipeline + parameters │ viewer (slices / NiiVue) │ outputs │
 *   └─────────────────────────────────────────────────────┘
 *
 * Same stores, engine and visual language as the classic workstation (which
 * stays one click away); only the arrangement is simpler.
 */

import * as React from "react";
import { Box, Brain, Container, Github, Hexagon, ScanEye } from "lucide-react";

import { ErrorBoundary } from "@/shell/ErrorBoundary";
import { Splitter } from "@/shell/Splitter";
import { BrandMark } from "@/shell/BrandMark";
import { usePipelineSession } from "@/pipeline/session";
import { useJob } from "@/store/appStore";
import { OutputsPanel } from "./OutputsPanel";
import { PipelinePanel } from "./PipelinePanel";
import { useSimple } from "./store";
import { ViewerPanel } from "./ViewerPanel";

/** The rest of the OpenADS family, shown in the header. */
const RESOURCE_LINKS = [
  { href: "https://seg.strokefair.org", label: "SegWeb", title: "Stroke segmentation on the web", Icon: Brain },
  { href: "https://strokeview.strokefair.org/", label: "StrokeView", title: "StrokeView", Icon: ScanEye },
  { href: "https://github.com/farialab/OpenADS", label: "GitHub", title: "OpenADS source code", Icon: Github },
  { href: "https://hub.docker.com/r/sljhlab/openads", label: "Docker", title: "OpenADS Docker image (sljhlab/openads)", Icon: Container },
  { href: "https://github.com/neurodesk/neurocontainers/tree/main/recipes/openadscpu", label: "Neurodesk", title: "OpenADS (CPU) recipe in Neurodesk", Icon: Box },
] as const;

/** Keep the outputs list in step with the run, and show its result when it ends. */
function useRunFollower() {
  const job = useJob();
  const progress = job ? `${job.jobId}:${job.state}:${job.progressPct}` : "";
  const settled = job && job.state !== "running" && job.state !== "queued";

  React.useEffect(() => {
    void useSimple.getState().refreshOutputs();
  }, [progress]);

  React.useEffect(() => {
    if (!job || !settled || job.state !== "completed") return;
    const prefix = `${job.jobId}/`;
    void useSimple
      .getState()
      .refreshOutputs()
      .then(() => {
        const { outputs } = useSimple.getState();
        const mine = outputs.filter((f) => f.id.startsWith(prefix));
        const base =
          mine.find((f) => /_DWI_space-MNI152_aff\.nii/.test(f.name)) ??
          mine.find((f) => /_TTP_space-MNI152_aff\.nii/.test(f.name));
        const mask =
          mine.find((f) => /stroke-mask_space-MNI152/.test(f.name)) ??
          mine.find((f) => /_HP-mask_space-MNI152\.nii/.test(f.name));
        // Replace, not add: an input-space overlay would not line up with MNI.
        if (base) useSimple.setState({ baseId: base.id, overlayIds: mask ? [mask.id] : [] });
      });
  }, [job?.jobId, settled]);
}

const DEFAULT_WIDTHS = { left: 320, right: 400 };
const WIDTHS_KEY = "ads.simple.widths";

/** Column widths, remembered per browser; double-click a divider to reset it. */
function useColumnWidths() {
  const [widths, setWidths] = React.useState<{ left: number; right: number }>(() => {
    try {
      return { ...DEFAULT_WIDTHS, ...JSON.parse(localStorage.getItem(WIDTHS_KEY) ?? "{}") };
    } catch {
      return DEFAULT_WIDTHS;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths));
    } catch {
      // Not persisted in a private window; still works for this session.
    }
  }, [widths]);
  return [widths, setWidths] as const;
}

/** While a run is going, closing or reloading the tab asks first: the run lives in this page. */
function useGuardRunningJob(running: boolean) {
  React.useEffect(() => {
    if (!running) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [running]);
}

export function SimpleApp() {
  const subjectId = usePipelineSession((s) => s.draft.subjectId);
  const [widths, setWidths] = useColumnWidths();
  const job = useJob();
  useGuardRunningJob(job?.state === "running" || job?.state === "queued");
  useRunFollower();

  const status =
    job?.state === "running"
      ? `Running · ${job.progressPct}%`
      : job?.state === "completed"
        ? "Finished"
        : job?.state === "failed"
          ? "Failed"
          : "Ready";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-ads-bg text-ads-text">
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-ads-border bg-ads-surface px-4">
        <Hexagon size={14} className="text-ads-accent" strokeWidth={1.75} />
        <BrandMark />
        <span className="text-ads-border-strong">|</span>
        <span className="text-[12.5px] text-ads-muted">Acute stroke detection and segmentation</span>
        <div className="flex flex-1 items-center justify-center text-[12.5px]">
          {subjectId ? <span className="font-medium text-ads-accent">{subjectId}</span> : <span className="text-ads-muted">No subject open</span>}
        </div>
        <span
          className={
            job?.state === "failed"
              ? "text-[11.5px] text-red-600"
              : job?.state === "running"
                ? "text-[11.5px] text-ads-accent"
                : "text-[11.5px] text-ads-muted"
          }
        >
          {status}
        </span>
        <nav className="flex items-center gap-1" aria-label="Related OpenADS resources">
          {RESOURCE_LINKS.map(({ href, label, title, Icon }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={title}
              className="flex items-center gap-1 rounded border border-ads-border px-2 py-0.5 text-[11px] text-ads-muted hover:border-ads-accent-border hover:bg-ads-hover hover:text-ads-text"
            >
              <Icon className="h-3 w-3" />
              {label}
            </a>
          ))}
        </nav>
      </header>

      <main className="flex min-h-0 flex-1">
        <aside className="shrink-0" style={{ width: widths.left }}>
          <ErrorBoundary label="Pipeline">
            <PipelinePanel />
          </ErrorBoundary>
        </aside>
        <Splitter
          orientation="vertical"
          edge="start"
          size={widths.left}
          min={260}
          max={560}
          onResize={(left) => setWidths((w) => ({ ...w, left }))}
          onDoubleClick={() => setWidths((w) => ({ ...w, left: DEFAULT_WIDTHS.left }))}
        />
        <section className="min-w-0 flex-1">
          <ErrorBoundary label="Viewer">
            <ViewerPanel />
          </ErrorBoundary>
        </section>
        <Splitter
          orientation="vertical"
          edge="end"
          size={widths.right}
          min={300}
          max={900}
          onResize={(right) => setWidths((w) => ({ ...w, right }))}
          onDoubleClick={() => setWidths((w) => ({ ...w, right: DEFAULT_WIDTHS.right }))}
        />
        <aside className="shrink-0" style={{ width: widths.right }}>
          <ErrorBoundary label="Outputs">
            <OutputsPanel />
          </ErrorBoundary>
        </aside>
      </main>

      <footer className="flex h-6 shrink-0 items-center gap-3 border-t border-ads-border bg-ads-surface px-4 text-[10.5px] text-ads-faint">
        <span>Browser · WASM — everything runs on this machine, nothing is uploaded.</span>
        <span className="ml-auto">OpenADS · DAGMNet · SynthStrip · ANTs · JHU-ICBM. For research and review, not a diagnosis.</span>
      </footer>
    </div>
  );
}
